import React from 'react'

import { pathOr } from 'ramda'
import styled from 'styled-components'

import SearchBox from 'components/SearchBox'
import Spinner from 'components/Spinner'
import { TrackClickProps } from 'components/trackers'
import { Heading } from 'components/typography'
import { SpendAccount, User } from 'generated/types'
import {
  DirectDepositCard,
  Divider,
  HeroText,
  HeroTitle,
  StyledHero,
  StyledHeroContainer,
} from 'pages/spend/direct-deposit/SharedComponents'
import { WebViewProps } from 'pages/spend/direct-deposit/WebViewComponents'
import { theme } from 'theme'
import { SEARCH_CONTAINER } from 'utils/testing/testIDs'

import SearchResultItem, { AddEmployerItem } from './SearchResultItem'

export type GqlProps = {
  loading: boolean
  searchEmployers: any[]
  user: User
  spendAccounts?: any[]
  spendAccount?: SpendAccount
}

export type Props = {
  data?: GqlProps
  loading: boolean
  searching: boolean
  step: Step
  results: any[]
  searchTerm: string
  heroSubtitle: string
  bankAccountId: string
  trackingId: string
  toggles: any
  setStep: (step: Step) => void
  setSerachTerm: (searchTerm: string) => void
  setResults: (results: any[]) => void
  setHeroSubtitle: (heroSubtitle: string) => void
  setTrackingId: (trackingId: string) => void
  setSearching: (searching: boolean) => void
  handleSearch: (searchTerm: string) => void
  handleAddEmployer: () => void
  handleResultClick: () => void
  handleActionStripBack: () => void
  location: any
  topEmployersList: Employer[]
  showTopEmployers: boolean
  analytics: any
}

export type InnerProps = Props & TrackClickProps

export type WVProps = Props & WebViewProps & TrackClickProps

export enum Step {
  Initial = 0,
  Results,
}

export type Employer = {
  employerName: string
  logo: string
}

export const subtitle = (step: Step, hasResults: boolean) => {
  if (step === Step.Initial) {
    return 'Search for the employer, holding company or payroll service that is on your paycheck.'
  } else if (hasResults) {
    return 'We found your employer! Set up direct deposit and get started with Spend.'
  } else {
    return "That employer isn't in our database. First, confirm that this is the company name on your paycheck. If so, you can add them below."
  }
}

export const Search = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 24px 48px 24px;

  @media (max-width: ${theme('breakpoints.md')}px) {
    display: block;
    padding: 24px 20px 24px;
  }
`

export const SearchLabel = styled(Heading)`
  line-height: 36px;
  flex: 1;

  @media (max-width: ${theme('breakpoints.md')}px) {
    display: block;
    padding-bottom: 24px;
  }
`

export const SearchResults = styled.div`
  padding: 40px 25px;
  width: 100%;
`

export const EmptySearch = styled.div`
  color: ${theme('colors.stoneLight')};
  font-size: 14px;
  padding: 42px 0;
  text-align: center;
`

export const Hero = (props: Props) => (
  <StyledHeroContainer>
    <StyledHero secondary={true}>
      <HeroTitle>What Company is on Your Paycheck?</HeroTitle>
      <HeroText>{props.heroSubtitle}</HeroText>
    </StyledHero>
  </StyledHeroContainer>
)

export const SearchPanel = (props: Props) => (
  <DirectDepositCard
    data-testloading={props.data.loading}
    data-testid={SEARCH_CONTAINER}
  >
    <Search>
      <SearchLabel>
        Suggested Employers{' '}
        {props.results && props.results.length
          ? `(${props.results.length})`
          : ''}
      </SearchLabel>
      <SearchBox onSearch={props.handleSearch} />
    </Search>
    <Divider />
    <SearchResults>
      {props.step === Step.Results &&
        !props.searching &&
        props.results &&
        props.results.length === 0 && (
          <EmptySearch>No results found</EmptySearch>
        )}

      {props.step === Step.Results && props.searching && <Spinner />}

      {props.step === Step.Results &&
        !props.searching &&
        props.results &&
        props.results.length > 0 &&
        props.results.map((result, index) => (
          <SearchResultItem
            key={result.id}
            employerId={result.id}
            employerName={result.employerName}
            employerCount={props.results.length}
            employerIndex={index}
            requiresEmployerMailingAddress={pathOr(
              null,
              ['switch', 'requiresEmployerMailingAddress'],
              result,
            )}
            switchType={result.switchType}
            ctaUrl={result.switchType === 'online' ? result.switch.url : ''}
            trackingId={props.trackingId}
            onClick={props.handleResultClick}
            logoUrl={result.logoUrl}
            clickSwitchTargetId={
              result.switchType === 'online'
                ? result.switch.onlineVendorTargetId
                : result.switch.submittableVendorTargetId
            }
          />
        ))}

      {props.step === Step.Results && !props.searching && (
        <AddEmployerItem onClick={props.handleAddEmployer} />
      )}
    </SearchResults>
  </DirectDepositCard>
)
